/**
 * Weekly Off & Monthly Off — one calendar-first settings step.
 *
 * Saves through the existing PATCH `/organisation/me/hr-settings` (`attendance`):
 *   week_off_weekdays  — Mon=0 … Sun=6, off every week
 *   monthly_off_rules  — [{ occurrence: "1st"|"2nd"|"3rd"|"4th"|"Last", weekday: "saturday" }]
 * The backend (`org_week_off_policy`) validates the rules and applies them to
 * attendance and payroll; the calendar below previews exactly that result.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarOff, ChevronLeft, ChevronRight, Loader2, Save, X } from "lucide-react";

import API from "../../services/api";
import { useToast } from "../../Moduels/Context/ToastContext";
import {
  monthlyOffRulesFromAttendance,
  weekOffWeekdaysFromAttendance,
  weekdayOccurrence,
} from "../../Moduels/attendance/attendanceCalendarUtils";
// eslint-disable-next-line no-unused-vars -- budget explainer hidden for now (see below)
import WeeklyOffBudgetPanel from "./WeeklyOffBudgetPanel";
import ConfirmDialog from "../ui/ConfirmDialog";
import { minOffsInMonth, shortMonthTrimNote, weeklyOffAllowance } from "./weeklyOffBudget";

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_KEYS = WEEKDAY_NAMES.map((d) => d.toLowerCase());
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const OCCURRENCE_ORDER = { "1st": 1, "2nd": 2, "3rd": 3, "4th": 4, Last: 5 };
const ORDINAL = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th" };

function fmtErr(e, fallback) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((i) => i?.msg || String(i)).join("; ");
  return fallback;
}

function fixedWorkingDaysFromSettings(data) {
  const hr = data?.hr_settings || {};
  const payroll = data?.payroll_settings || hr.payroll || {};
  if (String(payroll.total_working_days_mode || "").toLowerCase() !== "fixed") return null;
  const days = Number(hr.monthly_salary_divisor_days ?? payroll.monthly_salary_divisor_days);
  return Number.isFinite(days) && days > 0 ? days : null;
}

const ruleKey = (r) => `${r.occurrence}|${r.weekday}`;
const ruleLabel = (r) => `${r.occurrence} ${WEEKDAY_NAMES[WEEKDAY_KEYS.indexOf(r.weekday)]}`;

function sortRules(rules) {
  return [...rules].sort(
    (a, b) =>
      WEEKDAY_KEYS.indexOf(a.weekday) - WEEKDAY_KEYS.indexOf(b.weekday) ||
      OCCURRENCE_ORDER[a.occurrence] - OCCURRENCE_ORDER[b.occurrence]
  );
}

/** Monthly off rules that this date matches (e.g. a 4th Saturday that is also the last one). */
function matchingRules(rules, weekdayKey, occ) {
  return rules.filter(
    (r) =>
      r.weekday === weekdayKey &&
      (r.occurrence === "Last" ? occ.isLast : OCCURRENCE_ORDER[r.occurrence] === occ.index)
  );
}

/** Mon-first month grid: leading blanks, then one cell per day. */
function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7; // Monday = 0
  const dim = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: lead }, (_, i) => ({ key: `b${i}`, blank: true }));
  for (let day = 1; day <= dim; day += 1) {
    const mon0 = (lead + day - 1) % 7;
    cells.push({ key: `d${day}`, day, mon0, occ: weekdayOccurrence(year, month, day) });
  }
  return cells;
}

function workWeekFor(weekly) {
  const key = [...weekly].sort().join(",");
  if (key === "5,6") return { work_week: "mon-fri", week_off_option: "sat-sun" };
  if (key === "6") return { work_week: "mon-sat", week_off_option: "sunday" };
  return { work_week: "custom", week_off_option: "custom" };
}

function OffDialog({ dialog, onClose, onAdd, onRemove }) {
  if (!dialog) return null;
  const { dateLabel, weekdayName, kind, options, matches } = dialog;
  const body = (
    <div className="als-woc-overlay" role="dialog" aria-modal="true" aria-labelledby="als-woc-dlg-title" onClick={onClose}>
      <div className="als-woc-dialog" onClick={(e) => e.stopPropagation()}>
        <p className="als-woc-dialog-date">{dateLabel}</p>

        {kind === "weekly" && (
          <>
            <h3 id="als-woc-dlg-title" className="als-woc-dialog-title">{weekdayName} is already a Weekly Off</h3>
            <p className="als-woc-dialog-text">
              {weekdayName} is already configured as an off day every week, so a Monthly Off is not required.
            </p>
            <div className="als-woc-dialog-actions">
              <button type="button" className="als-btn-primary" onClick={onClose}>Close</button>
            </div>
          </>
        )}

        {kind === "existing" && (
          <>
            <h3 id="als-woc-dlg-title" className="als-woc-dialog-title">
              {matches.map(ruleLabel).join(" & ")}
            </h3>
            <p className="als-woc-dialog-text">
              {matches.length > 1 ? "These are configured as Monthly Offs." : "This is configured as a Monthly Off."}
            </p>
            <div className="als-woc-dialog-actions">
              <button type="button" className="als-btn-ghost" onClick={onClose}>Cancel</button>
              {matches.map((r) => (
                <button key={ruleKey(r)} type="button" className="als-btn-danger" onClick={() => onRemove(r)}>
                  Remove {matches.length > 1 ? ruleLabel(r) : "Monthly Off"}
                </button>
              ))}
            </div>
          </>
        )}

        {kind === "add" && (
          <>
            <h3 id="als-woc-dlg-title" className="als-woc-dialog-title">Set as Monthly Off?</h3>
            {options.length === 1 ? (
              <p className="als-woc-dialog-text">
                This will make the <strong>{ruleLabel(options[0])}</strong> of every month an off day.
              </p>
            ) : (
              <p className="als-woc-dialog-text">
                This date is both the {ruleLabel(options[0])} and the {ruleLabel(options[1])} of this month. Choose
                which one should be off every month.
              </p>
            )}
            <div className="als-woc-dialog-actions">
              <button type="button" className="als-btn-ghost" onClick={onClose}>Cancel</button>
              {options.map((r) => (
                <button key={ruleKey(r)} type="button" className="als-btn-primary" onClick={() => onAdd(r)}>
                  {options.length > 1 ? `Every ${ruleLabel(r)}` : "Set Monthly Off"}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(body, document.body) : null;
}

/**
 * Plain-language reason the chosen off days do not fit the fixed working days.
 * Mirrors the backend check: in a 31-day month, weekly offs + one day per monthly
 * off must not exceed (31 − fixed working days).
 */
function offDaysBudgetError(weeklyIndexes, rules, fixed) {
  if (!fixed) return null;
  const allowance = weeklyOffAllowance(31, fixed);
  const weeklyMin = minOffsInMonth(weeklyIndexes, 31);
  const need = weeklyMin + rules.length;
  if (need <= allowance) return null;
  const parts = [];
  if (weeklyIndexes.length) {
    parts.push(`every ${weeklyIndexes.map((i) => WEEKDAY_NAMES[i]).join(" & ")} (at least ${weeklyMin} days)`);
  }
  if (rules.length) {
    parts.push(`${rules.map(ruleLabel).join(", ")} (${rules.length} day${rules.length > 1 ? "s" : ""})`);
  }
  const extra = need - allowance;
  return (
    `Too many off days. In a 31-day month, ${parts.join(" + ")} = ${need} off days, ` +
    `but working days are fixed at ${fixed}, which leaves only ${allowance} (31 − ${fixed}). ` +
    `Remove ${extra} off day${extra > 1 ? "s" : ""}, or change the working days.`
  );
}

export default function WeeklyMonthlyOffTab({ onConfigured, onCancel }) {
  const toast = useToast();
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekly, setWeekly] = useState(() => new Set());
  const [rules, setRules] = useState([]);
  const [saved, setSaved] = useState({ weekly: "", rules: "" });
  const [fixedWorkingDays, setFixedWorkingDays] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [confirmLeave, setConfirmLeave] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/organisation/me/hr-settings");
      const att = res.data?.attendance_settings || res.data?.hr_settings?.attendance || {};
      let weeklyDays = weekOffWeekdaysFromAttendance(att);
      const hasRuleConfig = Array.isArray(att.monthly_off_rules) || att.monthly_off_enabled;
      // Legacy "2nd & 4th Saturday" meant Saturday was NOT off every week.
      if (att.alternate_saturday && !hasRuleConfig) weeklyDays = weeklyDays.filter((d) => d !== 5);
      const nextWeekly = new Set(weeklyDays);
      const nextRules = sortRules(monthlyOffRulesFromAttendance(att));
      setWeekly(nextWeekly);
      setRules(nextRules);
      setSaved({
        weekly: [...nextWeekly].sort().join(","),
        rules: nextRules
          .filter((r) => !nextWeekly.has(WEEKDAY_KEYS.indexOf(r.weekday)))
          .map(ruleKey)
          .join(","),
      });
      setFixedWorkingDays(fixedWorkingDaysFromSettings(res.data));
      setSaveError("");
    } catch (e) {
      toast.error(fmtErr(e, "Failed to load weekly and monthly off settings"));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const weeklyIndexes = useMemo(() => [...weekly].sort(), [weekly]);
  // Rules on a weekday that is now off every week are redundant and are not saved.
  const effectiveRules = useMemo(
    () => rules.filter((r) => !weekly.has(WEEKDAY_KEYS.indexOf(r.weekday))),
    [rules, weekly]
  );
  // Each monthly off rule adds exactly one off day per month to the fixed-working-days budget.
  const patternError = offDaysBudgetError(weeklyIndexes, effectiveRules, fixedWorkingDays);
  // eslint-disable-next-line no-unused-vars -- used by the hidden budget explainer
  const trimNote = fixedWorkingDays && !patternError
    ? shortMonthTrimNote(weeklyIndexes, fixedWorkingDays, effectiveRules.length)
    : null;
  const isDirty =
    weeklyIndexes.join(",") !== saved.weekly || effectiveRules.map(ruleKey).join(",") !== saved.rules;

  const cells = useMemo(() => buildMonthCells(view.year, view.month), [view]);

  const shiftMonth = (delta) =>
    setView(({ year, month }) => {
      const d = new Date(year, month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const toggleWeekly = (idx) => {
    const next = new Set(weekly);
    if (next.has(idx)) {
      next.delete(idx);
    } else {
      next.add(idx);
      const covered = rules.filter((r) => r.weekday === WEEKDAY_KEYS[idx]);
      if (covered.length) {
        setRules((prev) => prev.filter((r) => r.weekday !== WEEKDAY_KEYS[idx]));
        toast.success(
          `${covered.map(ruleLabel).join(", ")} removed from Monthly Off — ${WEEKDAY_NAMES[idx]} is now off every week.`
        );
      }
    }
    setWeekly(next);
    setSaveError("");
  };

  const openDay = (cell) => {
    const date = new Date(view.year, view.month, cell.day);
    const dateLabel = date.toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
    const weekdayKey = WEEKDAY_KEYS[cell.mon0];
    const weekdayName = WEEKDAY_NAMES[cell.mon0];
    if (weekly.has(cell.mon0)) {
      setDialog({ kind: "weekly", dateLabel, weekdayName });
      return;
    }
    const matches = matchingRules(rules, weekdayKey, cell.occ);
    if (matches.length) {
      setDialog({ kind: "existing", dateLabel, weekdayName, matches });
      return;
    }
    // Only 1st–4th and Last exist; a 5th occurrence can only be "Last".
    const options = [];
    if (cell.occ.index <= 4) options.push({ occurrence: ORDINAL[cell.occ.index], weekday: weekdayKey });
    if (cell.occ.isLast) options.push({ occurrence: "Last", weekday: weekdayKey });
    setDialog({ kind: "add", dateLabel, weekdayName, options });
  };

  const addRule = (rule) => {
    if (rules.some((r) => ruleKey(r) === ruleKey(rule))) {
      toast.error(`${ruleLabel(rule)} is already configured as a Monthly Off.`);
    } else {
      setRules((prev) => sortRules([...prev, rule]));
      setSaveError("");
    }
    setDialog(null);
  };

  const removeRule = (rule) => {
    setRules((prev) => prev.filter((r) => ruleKey(r) !== ruleKey(rule)));
    setSaveError("");
    setDialog(null);
  };

  const handleSave = async () => {
    if (patternError) {
      setSaveError(patternError);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await API.patch("/organisation/me/hr-settings", {
        attendance: {
          ...workWeekFor(weekly),
          week_off_weekdays: weeklyIndexes,
          monthly_off_rules: effectiveRules,
        },
      });
      toast.success("Weekly Off & Monthly Off saved. Attendance and payroll now use these days.");
      await load();
      await onConfigured?.();
    } catch (e) {
      const msg = fmtErr(e, "Failed to save weekly and monthly off");
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="als-loading">
        <Loader2 size={22} className="als-spin" />
        Loading…
      </div>
    );
  }

  return (
    <div className="als-tab-body als-woc">
      <div className="als-section-header">
        <div className="als-section-icon">
          <CalendarOff size={18} />
        </div>
        <div>
          <h2 className="als-section-title">Weekly Off &amp; Monthly Off</h2>
          <p className="als-section-sub">Configure your organization&apos;s recurring weekly and monthly off days.</p>
        </div>
      </div>

      <div className="als-woc-layout">
        <section className="als-woc-calendar" aria-label="Off days calendar">
          <div className="als-woc-nav">
            <button type="button" className="als-woc-nav-btn" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <ChevronLeft size={18} />
            </button>
            <span className="als-woc-month">{MONTH_NAMES[view.month]}</span>
            <button type="button" className="als-woc-nav-btn" onClick={() => shiftMonth(1)} aria-label="Next month">
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="als-woc-grid" role="grid">
            {WEEKDAY_NAMES.map((d) => (
              <div key={d} className="als-woc-head" role="columnheader">
                <span className="als-woc-head-long">{d.slice(0, 3)}</span>
                <span className="als-woc-head-short" aria-hidden="true">{d.slice(0, 2)}</span>
              </div>
            ))}
            {cells.map((cell) => {
              if (cell.blank) return <div key={cell.key} className="als-woc-cell als-woc-cell--blank" />;
              const isWeekly = weekly.has(cell.mon0);
              const isMonthly = !isWeekly && matchingRules(rules, WEEKDAY_KEYS[cell.mon0], cell.occ).length > 0;
              const isToday =
                cell.day === today.getDate() && view.month === today.getMonth() && view.year === today.getFullYear();
              const state = isWeekly ? "weekly" : isMonthly ? "monthly" : "work";
              const stateText = isWeekly ? "Weekly Off" : isMonthly ? "Monthly Off" : "Working day";
              return (
                <button
                  key={cell.key}
                  type="button"
                  className={`als-woc-cell als-woc-cell--${state}${isToday ? " als-woc-cell--today" : ""}`}
                  onClick={() => openDay(cell)}
                  aria-label={`${cell.day} ${MONTH_NAMES[view.month]}, ${WEEKDAY_NAMES[cell.mon0]} — ${stateText}`}
                >
                  <span className="als-woc-day">{cell.day}</span>
                  {state !== "work" && <span className="als-woc-dot" aria-hidden="true" />}
                </button>
              );
            })}
          </div>

          <div className="als-woc-legend">
            <span><i className="als-woc-swatch als-woc-swatch--weekly" /> Weekly Off</span>
            <span><i className="als-woc-swatch als-woc-swatch--monthly" /> Monthly Off</span>
            <span><i className="als-woc-swatch als-woc-swatch--work" /> Working day</span>
          </div>
        </section>

        <div className="als-woc-side">
          <section className="als-woc-panel">
            <h3 className="als-woc-panel-title">Weekly Off</h3>
            <p className="als-woc-panel-sub">Select the days that are automatically off every week.</p>
            <div className="als-woc-weekdays">
              {WEEKDAY_NAMES.map((d, i) => (
                <label key={d} className={`als-woc-weekday${weekly.has(i) ? " als-woc-weekday--on" : ""}`}>
                  <input type="checkbox" checked={weekly.has(i)} onChange={() => toggleWeekly(i)} />
                  <span>{weekly.has(i) ? `All ${d}s` : d}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="als-woc-panel">
            <h3 className="als-woc-panel-title">Monthly Off</h3>
            <p className="als-woc-panel-sub">
              Click a date on the calendar to make that weekday&apos;s occurrence (for example, the 2nd Saturday) off
              every month.
            </p>
            <div className="als-woc-current-label">Current Monthly Off</div>
            {effectiveRules.length === 0 ? (
              <p className="als-woc-empty">No Monthly Off yet.</p>
            ) : (
              <ul className="als-woc-rules">
                {effectiveRules.map((r) => (
                  <li key={ruleKey(r)} className="als-woc-rule">
                    <span>{ruleLabel(r)} <span className="als-woc-rule-hint">of every month</span></span>
                    <button
                      type="button"
                      className="als-woc-rule-remove"
                      onClick={() => removeRule(r)}
                      aria-label={`Remove ${ruleLabel(r)}`}
                      title={`Remove ${ruleLabel(r)}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Working-days budget explainer hidden; only a blocking error is shown.
      {fixedWorkingDays ? (
        <WeeklyOffBudgetPanel workingDays={fixedWorkingDays} note={trimNote} error={patternError} />
      ) : null}
      */}
      {patternError ? <div className="als-woc-error" role="alert">{patternError}</div> : null}

      {saveError ? <div className="als-woc-error" role="alert">{saveError}</div> : null}

      <div className="als-footer als-woc-footer">
        {patternError ? (
          <p className="als-woc-save-hint als-woc-save-hint--warn">
            Remove some weekly or monthly off days to enable Save.
          </p>
        ) : !isDirty ? (
          <p className="als-woc-save-hint">No changes to save.</p>
        ) : null}
        {onCancel ? (
          <button
            type="button"
            className="als-btn-ghost"
            onClick={() => (isDirty ? setConfirmLeave(true) : onCancel())}
            disabled={saving}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="als-btn-primary"
          onClick={handleSave}
          disabled={saving || !isDirty || Boolean(patternError)}
        >
          {saving ? <Loader2 size={14} className="als-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <OffDialog dialog={dialog} onClose={() => setDialog(null)} onAdd={addRule} onRemove={removeRule} />
      <ConfirmDialog
        open={confirmLeave}
        title="Discard unsaved changes?"
        message="Your weekly and monthly off changes have not been saved. Leave this page without saving?"
        confirmLabel="Discard & exit"
        cancelLabel="Keep editing"
        danger
        onConfirm={() => {
          setConfirmLeave(false);
          onCancel?.();
        }}
        onCancel={() => setConfirmLeave(false)}
      />
    </div>
  );
}
